import { Axiom } from "@axiomhq/js";
import {
	type CommonProps,
	type ILogForwarder,
	type IRemoteLogForwarder,
	type LogEntry,
	LogLevel,
	type LogMessage,
	type LogMetadata,
	type LogType,
} from "../types";
import { errorIdentityOf } from "../utils/error-name";

export class AxiomLogger implements ILogForwarder, IRemoteLogForwarder {
	axiom: Axiom;
	dataset: string;

	// Process-wide dedup so a misconfig or persistent transport failure surfaces
	// once per `${name}:${message}`, not on every ingest attempt; `message` can
	// carry variable content, so this is bounded to MAX_SEEN_ERRORS via simple
	// insertion-order eviction (a `Set` iterates oldest-first).
	private static readonly seenErrors = new Set<string>();
	private static readonly MAX_SEEN_ERRORS = 200;

	// `@axiomhq/js` routes all transport failures to its `onError` callback
	// (which defaults to `console.error`). Two of those are noise that cannot
	// stop upstream: a fetch-timeout DOMException, and a JSON parse failure
	// when Axiom returns an empty response body. Suppress those; report
	// anything else once per process so real config/auth failures still
	// surface.
	private static handleAxiomError = (error: unknown): void => {
		if (error instanceof Error) {
			if (error.name === "TimeoutError") return;
			if (error instanceof SyntaxError && error.message === "Unexpected end of JSON input") return;
			const key = `${error.name}:${error.message}`;
			if (AxiomLogger.seenErrors.has(key)) return;
			if (AxiomLogger.seenErrors.size >= AxiomLogger.MAX_SEEN_ERRORS) {
				const oldest = AxiomLogger.seenErrors.values().next().value;
				if (oldest !== undefined) {
					AxiomLogger.seenErrors.delete(oldest);
				}
			}
			AxiomLogger.seenErrors.add(key);
			console.error("[AxiomLogger]", { name: error.name, message: error.message });
			return;
		}
		console.error("[AxiomLogger]", error);
	};

	constructor(axiomToken: string, axiomOrgId: string, axiomDataset: string) {
		this.axiom = new Axiom({
			token: axiomToken,
			orgId: axiomOrgId,
			onError: AxiomLogger.handleAxiomError,
		});
		this.dataset = axiomDataset;
		if (!axiomDataset || !axiomOrgId || !axiomToken) console.error("Invalid axiom parameters!");
	}

	public transformLog(
		type: LogType,
		level: LogLevel,
		injectedProps: CommonProps,
		message: LogMessage,
		...metadata: LogMetadata[]
	): LogEntry {
		let entry: LogEntry = {
			level: LogLevel[level],
			type,
		};

		if (typeof message === "string") {
			entry.message = message;
		} else if (typeof message === "object") {
			entry = { ...entry, ...message };
		} else {
			entry.message = String(message);
		}

		if (metadata?.length > 0) {
			const processedMetadata = metadata.map((item) => {
				if (item instanceof Error) {
					// A raw `JSON.stringify(item, getOwnPropertyNames)` would ship the full
					// stack (file paths) plus any custom own property on the error, secret-named
					// or not, straight to Axiom. `errorIdentityOf` is the
					// CLIENT collapsing boundary this whole helper module exists to
					// enforce (see utils/error-name.ts); the transport must go through it
					// too, not re-derive its own ad-hoc shape.
					const { errorName, errorMessage } = errorIdentityOf(item);
					return { errorName, errorMessage };
				}
				return item;
			});
			entry.metadata = processedMetadata;
		}

		entry = {
			...entry,
			...injectedProps,
		};

		entry.message = `[${entry.project ?? "unknown"}][${type}] ${entry.message ? entry.message : ""}`;
		return entry;
	}

	log(
		type: LogType,
		level: LogLevel,
		injectedProps: CommonProps,
		message: LogMessage,
		...metadata: LogMetadata[]
	): void {
		const entry = this.transformLog(type, level, injectedProps, message, ...metadata);
		this.logEntry(entry);
	}

	logEntry(entry: LogEntry): void {
		try {
			void Promise.resolve(this.axiom.ingest(this.dataset, entry)).catch((): undefined => undefined);
		} catch {
			// Log shipping failures must not surface as caller errors.
		}
	}

	public async flushLogs(): Promise<void> {
		try {
			await this.axiom.flush();
		} catch {
			// Log shipping failures must not surface as caller errors.
		}
	}
}
